import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Zod from 'zod';
import { useTranslation } from 'react-i18next';

import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { TextField } from 'common/forms/Field/TextField';
import { getSafeLinkUrl } from 'common/components/ContentEditor/utils/editorHelpers';

interface IProps {
    open: boolean;
    initialValue?: string;
    onClose(): void;
    onSubmit(url: string): void;
}

export const LinkDialog = ({
    open,
    initialValue,
    onClose,
    onSubmit,
}: IProps) => {
    const [translate] = useTranslation();

    // Without this, a `javascript:`/`data:` URL would pass through
    // untouched and get rendered as `<a href="javascript:...">`. This
    // accepts exactly what EditorLink renders: the dialog is prefilled with
    // the url of the link being edited, so rejecting a value that renders
    // fine would leave the editor unable to save it at all.
    const schema = Zod.object({
        url: Zod.string().refine(
            (value) => !value || !!getSafeLinkUrl(value),
            translate('urlNotValid')
        ),
    });

    const { control, handleSubmit, reset } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { url: initialValue || '' },
    });

    // Reset only on the closed-to-open transition, not on every
    // `initialValue` change while already open — otherwise an unrelated
    // update to the source value would clobber in-progress user input.
    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) reset({ url: initialValue || '' });
        wasOpen.current = open;
    }, [open, initialValue, reset]);

    const submit = handleSubmit(({ url }) => onSubmit(url));

    return (
        <AlertDialog
            fullWidth
            actions={
                <>
                    <Button
                        autoFocus
                        color="primary"
                        variant="contained"
                        onClick={submit}
                    >
                        {translate('save')}
                    </Button>
                    <Button variant="contained" onClick={onClose}>
                        {translate('cancel')}
                    </Button>
                </>
            }
            maxWidth="sm"
            open={open}
            title={translate('editor.linkDialogTitle')}
            onClose={onClose}
        >
            <TextField
                autoFocus
                control={control}
                label={translate('editor.enterLinkUrl')}
                name="url"
                placeholder="https://..."
            />
        </AlertDialog>
    );
};
